import configs from '~/env.json'
import React, { lazy, useEffect } from 'react'
import { Switch, Redirect } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import PreviewMode from '~/components/preview-mode'
import ConnectivityGuard from '~/pages/premiacao/components/connectivity-guard'

import Route from './Route'

import { ThemeProvider } from '@material-ui/core/styles'
import CssBaseline from '@material-ui/core/CssBaseline'

import { FloraProvider } from '@grupoboticario/flora-react'

import DynamicRoute from './Dynamic'

import ErroAcesso from '~/pages/dynamic/ERRO_ACESSO'
import PremiacaoSemConexao from '~/pages/premiacao/sem-conexao'

import { isEmbedMode } from '~/helpers/contentMode'
import { MODALITIES } from '~/helpers/constants'
import { getFloraThemeOptions, getMuiThemeOptions } from '~/helpers/themeSelector'
import { useCanonicalLink } from '~/hooks/useCanonicalLink'

import { setPreviewLayoutCampaign } from '~/store/modules/config/actions'

const DownloadApp = lazy(() => import('~/pages/download-app'))
const Initial = lazy(() => import('~/pages/initial'))
const AppEntry = lazy(() => import('~/pages/app/entry'))
const AppCadastro = lazy(() => import('~/pages/app/cadastro'))
const AppCompletarCadastro = lazy(() => import('~/pages/app/completar-cadastro'))
const AppConsumidorResgatouBrinde = lazy(() => import('~/pages/app/consumidor-resgatou-brinde'))
const AppConsumidorNaoElegivel = lazy(() => import('~/pages/app/consumidor-nao-elegivel'))
const AppConsumidorMenorDeIdade = lazy(() => import('~/pages/app/consumidor-menor-de-idade'))
const PremiacaoIdentificacao = lazy(() => import('~/pages/premiacao/identificacao'))
const PremiacaoCadastro = lazy(() => import('~/pages/premiacao/cadastro'))
const PremiacaoSistemaIndisponivel = lazy(() => import('~/pages/premiacao/sistema-indisponivel'))
const PremiacaoCadastroConcluido = lazy(() => import('~/pages/premiacao/cadastro-concluido'))
const PremiacaoParticipacaoGarantida = lazy(() => import('~/pages/premiacao/participacao-garantida'))

const Routes = () => {
  const dispatch = useDispatch()

  useCanonicalLink()

  const appAccess = useSelector((state) => state.campaignReducers.appAccess)
  const blockFront = useSelector((state) => state.campaignReducers.blockFront)
  const campaignReady = useSelector((state) => state.campaignReducers.campaignReady)
  const contentMode = useSelector((state) => state.configReducers.contentMode)
  const previewLayoutCampaign = useSelector((state) => state.configReducers.previewLayoutCampaign)

  const { branch, campaignId, campaignName, layout, modality } = useSelector((state) => state.campaignReducers.campaign)

  useEffect(() => {
    if (campaignName) {
      document.querySelector('head title').text = campaignName
    }

    if (layout?.COR_FUNDO) {
      document.querySelector('head [name="theme-color"]').content = layout.COR_FUNDO
    }
  }, [campaignId, campaignName, layout, previewLayoutCampaign])

  useEffect(() => {
    window.addEventListener(
      'message',
      (event) => {
        if (event.data == null || event.origin !== configs?.REACT_BACKOFFICE_MARKETING) {
          return
        }

        const { layoutObject } = JSON.parse(event.data)

        dispatch(setPreviewLayoutCampaign({ layoutPreview: layoutObject }))
      },
      false
    )
  }, [dispatch])

  const hasPreviewLayoutCampaign = (previewLayoutCampaign && Object.keys(previewLayoutCampaign).length) || false

  const muiTheme = getMuiThemeOptions(branch, hasPreviewLayoutCampaign ? previewLayoutCampaign : null, layout)
  const floraTheme = getFloraThemeOptions(branch)

  if (campaignId && layout?.REDIRECIONAR) {
    document.location.href = layout.REDIRECIONAR
  }

  return (
    <ThemeProvider theme={muiTheme}>
      <FloraProvider theme={floraTheme}>
        <CssBaseline />
        {isEmbedMode(contentMode) && <PreviewMode />}
        {!campaignReady ? (
          <Switch>
            <Route path='/' exact render={() => <Redirect to='/CAMPANHA_INVALIDA/ERRO_ACESSO' />} />
            <Route path='/:campaignId/ERRO_ACESSO' exact component={ErroAcesso} />
            <Route path='/:campaignId' component={Initial} />
          </Switch>
        ) : modality === MODALITIES.PRIZE ? (
          <>
            <ConnectivityGuard fallback={<PremiacaoSemConexao />}>
              <Switch>
                <Route path='/:campaignId/IDENTIFICACAO' exact component={PremiacaoIdentificacao} />
                <Route path='/:campaignId/CADASTRO' exact component={PremiacaoCadastro} />
                <Route path='/:campaignId/SISTEMA_INDISPONIVEL' exact component={PremiacaoSistemaIndisponivel} />
                <Route path='/:campaignId/CADASTRO_CONCLUIDO' exact component={PremiacaoCadastroConcluido} />
                <Route path='/:campaignId/PARTICIPACAO_GARANTIDA' exact component={PremiacaoParticipacaoGarantida} />
              </Switch>
            </ConnectivityGuard>
          </>
        ) : (
          <Switch>
            <Route path='/:campaignId' exact component={Initial} />
            <Route path='/:campaignId/APP_ENTRY' exact component={AppEntry} />
            <Route path='/:campaignId/APP_CADASTRO' exact component={AppCadastro} />
            <Route path='/:campaignId/APP_COMPLETAR_CADASTRO' exact component={AppCompletarCadastro} />
            <Route path='/:campaignId/APP_CONSUMIDOR_RESGATOU_BRINDE' exact component={AppConsumidorResgatouBrinde} />
            <Route path='/:campaignId/APP_CONSUMIDOR_NAO_ELEGIVEL' exact component={AppConsumidorNaoElegivel} />
            <Route path='/:campaignId/APP_CONSUMIDOR_MENOR_DE_IDADE' exact component={AppConsumidorMenorDeIdade} />
            <Route path='/:campaignId/:slug' component={blockFront && !appAccess ? DownloadApp : DynamicRoute} />
          </Switch>
        )}
      </FloraProvider>
    </ThemeProvider>
  )
}

export default Routes
